import type { WikiItem } from '../lib/feed'
import { relativeTime } from '../lib/feed'
import { Card, CardLink } from './Card'
import { useHubLang } from '../lib/i18n'
import { WIKI, HOME } from '../lib/hubCopy'
import { ConeTecMark } from './icons'

/**
 * Recent Wiki One answers, read from the database.
 *
 * Answers are shown with the answerer's name. The PUBLISHED ANSWER still never
 * names who asked: `content_wiki_answer` has an owner_person_id and no asker
 * column, public.wiki_answers_recent() does not select one because there is
 * none to select, and an invariant test in wiki_question_intake.sql fails if
 * one ever appears. That part of the original design is intact and load-
 * bearing, and this component must never read an asker even if one turns up.
 *
 * ── WHAT CHANGED ON 8 SEP, AND WHY THE NOTE BELOW IS DIFFERENT ──────────────
 *
 * This card used to end with "Questions are asked without a name attached."
 * That sentence was true until migration 032, which records the asker on
 * wiki_question — David's call, taken with the alternatives stated, so that
 * somebody can be told their question was answered.
 *
 * The sentence therefore came down. It was not softened or moved lower; a
 * privacy promise that has quietly become false is worse than no promise at
 * all, which is the entire lesson of B15 ("accurate and misleading" is the
 * failure mode). The accurate statement now lives in the ask box on #/wiki,
 * where somebody reads it BEFORE they type rather than after: their name is
 * recorded, the Wiki One team sees it, the published answer never carries it.
 *
 * Who can see a question is not a matter of good intentions — wiki_question's
 * read policy shows a row to its asker and to wiki.curate holders and to
 * nobody else.
 */
export function WikiAnswers({
  answers,
  locale,
}: {
  answers: WikiItem[]
  locale: string
}) {
  const lang = useHubLang()
  const w = WIKI[lang]
  const h = HOME[lang]
  return (
    <Card
      className="wikicard"
      title="Wiki One"
      // SHL-13. The count first, the link last — the link is the thing that has
      // to sit in the same corner on every card in the rail.
      action={
        <>
          <span className="pill">{w.recent(answers.length)}</span>
          <CardLink to="#/wiki" label={w.openWiki} />
        </>
      }
    >
      {answers.length === 0 ? (
        <p className="empty">{w.noAnswers}</p>
      ) : (
        <ul className="wiki">
          {answers.map((a) => (
            <li className="wiki__item" key={a.contentId}>
              <p className="wiki__q">
                {/* 112. The rail has no room for "Show original"; the mark and
                    its words say a machine translated this question, and the
                    row it links to offers the original. */}
                {a.translationStatus === 'machine' && (
                  <span className="wiki__translated" title={h.translatedTitle}>
                    <ConeTecMark size={10} />
                    <span className="sr-only">{h.translatedByOne}</span>
                  </span>
                )}
                {a.question}
              </p>
              <p className="wiki__meta">
                {a.answeredBy ? w.answeredBy(a.answeredBy) : w.answered}
                {a.lastReviewedAt && (
                  <> · {w.reviewed(relativeTime(a.lastReviewedAt, locale))}</>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="cardnote">
        {w.cardNote}
      </p>
    </Card>
  )
}
